import { useMemo, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { SectionCard } from './shared';
import { TraderPnlChart } from './charts/TraderPnlChart';
import {
    useAggregatePnlSeries,
    maxDrawdown,
    avgPairwiseCorrelation,
} from '../api/useAggregatePnlSeries';
import { computeGlobalDomain } from '../api/useTraderProfile';
import type { FlowPoint } from '../api/useTraderProfile';
import type { GlobalWatchlistMember } from '../api/useGlobalWatchlists';
import { cleanAccount } from '../utils/traderFormat';

const NO_FLOWS: FlowPoint[] = [];

function fmtSignedUsd(v: number | null): string {
    if (v == null) return '—';
    const a = Math.abs(v);
    const s = v < 0 ? '−' : '+';
    if (a >= 1_000_000) return `${s}$${(a / 1_000_000).toFixed(2)}M`;
    if (a >= 1_000) return `${s}$${(a / 1_000).toFixed(1)}k`;
    return `${s}$${a.toFixed(0)}`;
}

function fmtUsd(v: number | null): string {
    if (v == null) return '—';
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
}

function fmtPct(v: number | null): string {
    if (v == null) return '—';
    return `${(v * 100).toFixed(1)}%`;
}

function fmtCount(v: number): string {
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(v);
}

function fmtCorr(v: number | null): string {
    if (v == null) return '—';
    return v.toFixed(2);
}

function Stat({
    label,
    value,
    color,
    title,
}: {
    label: string;
    value: string;
    color?: string;
    title?: string;
}) {
    return (
        <Box sx={{ minWidth: 88 }} title={title}>
            <Typography
                sx={{
                    fontSize: '9.5px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '15px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: color ?? 'text.primary',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

export function AggregatePanel({
    members,
    onClear,
}: {
    members: GlobalWatchlistMember[];
    onClear: () => void;
}) {
    const theme = useTheme();
    const [mode, setMode] = useState<'pnl' | 'portfolio'>('pnl');
    const [zoom, setZoom] = useState<[Date, Date] | null>(null);

    const addresses = useMemo(
        () => members.map(m => cleanAccount(m.address)),
        [members]
    );

    const { aligned, combined, loading, error } =
        useAggregatePnlSeries(addresses);

    const scalar = useMemo(() => {
        let totalPnl = 0;
        let totalPort = 0;
        let anyPort = false;
        let totalTrades = 0;
        let totalMarkets = 0;
        let wrNum = 0;
        let wrDen = 0;
        let edgeNum = 0;
        let edgeDen = 0;
        for (const m of members) {
            if (m.total_pnl != null) totalPnl += m.total_pnl;
            if (m.portfolio_value != null) {
                totalPort += m.portfolio_value;
                anyPort = true;
            }
            if (m.num_trades != null) totalTrades += m.num_trades;
            if (m.num_markets != null) totalMarkets += m.num_markets;
            if (m.win_rate != null && m.num_trades != null) {
                wrNum += m.win_rate * m.num_trades;
                wrDen += m.num_trades;
            }
            if (m.avg_edge != null && m.num_trades != null) {
                edgeNum += m.avg_edge * m.num_trades;
                edgeDen += m.num_trades;
            }
        }
        return {
            totalPnl,
            portfolioValue: anyPort ? totalPort : null,
            totalTrades,
            totalMarkets,
            weightedWinRate: wrDen > 0 ? wrNum / wrDen : null,
            weightedAvgEdge: edgeDen > 0 ? edgeNum / edgeDen : null,
        };
    }, [members]);

    const dd = useMemo(() => maxDrawdown(combined), [combined]);
    const corr = useMemo(
        () => (aligned ? avgPairwiseCorrelation(aligned) : null),
        [aligned]
    );

    const globalDomain = useMemo(
        () => computeGlobalDomain(combined, combined, null, NO_FLOWS),
        [combined]
    );

    const pnlColor =
        scalar.totalPnl >= 0
            ? theme.palette.success.main
            : theme.palette.error.main;

    const chipSx = (active: boolean) => ({
        px: 1,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '10px',
        fontWeight: active ? 700 : 400,
        borderRadius: '4px',
        border: 1,
        borderColor: active ? 'primary.main' : 'divider',
        color: active ? 'primary.main' : 'text.disabled',
        bgcolor: active ? 'action.selected' : 'transparent',
        cursor: 'pointer',
        userSelect: 'none',
    });

    return (
        <SectionCard sx={{ mt: 1.5, mb: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                    Aggregated performance
                </Typography>
                <Typography
                    sx={{ fontSize: '11px', color: 'text.secondary' }}
                >
                    {members.length} accounts
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.5, mr: 0.5 }}>
                    <Box sx={chipSx(mode === 'pnl')} onClick={() => setMode('pnl')}>
                        PnL
                    </Box>
                    <Box
                        sx={chipSx(mode === 'portfolio')}
                        onClick={() => setMode('portfolio')}
                    >
                        Portfolio
                    </Box>
                </Box>
                <Box
                    component="span"
                    onClick={onClear}
                    sx={{
                        display: 'inline-flex',
                        opacity: 0.4,
                        cursor: 'pointer',
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 15 }} />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2.5,
                    px: 1.5,
                    py: 1.25,
                }}
            >
                <Stat
                    label="Combined PnL"
                    value={fmtSignedUsd(scalar.totalPnl)}
                    color={pnlColor}
                />
                <Stat
                    label="Portfolio"
                    value={fmtUsd(scalar.portfolioValue)}
                />
                <Stat label="Σ Trades" value={fmtCount(scalar.totalTrades)} />
                <Stat
                    label="Win rate (wtd)"
                    value={fmtPct(scalar.weightedWinRate)}
                />
                <Stat
                    label="Avg edge (wtd)"
                    value={fmtPct(scalar.weightedAvgEdge)}
                />
                <Stat
                    label="Max drawdown"
                    value={fmtUsd(dd.abs)}
                    color={theme.palette.error.main}
                    title="Largest peak-to-trough give-back of the combined cumulative PnL curve"
                />
                <Stat
                    label="Avg corr"
                    value={fmtCorr(corr)}
                    title="Mean pairwise correlation of daily PnL changes. Lower = more diversification benefit from running these together."
                />
                <Stat
                    label="Σ Markets"
                    value={fmtCount(scalar.totalMarkets)}
                    title="Summed across accounts — may double-count markets traded by more than one account"
                />
            </Box>

            <Box sx={{ px: 1.5, pb: 1.5, minHeight: 300 }}>
                {loading ? (
                    <Skeleton
                        variant="rectangular"
                        height={280}
                        sx={{ borderRadius: 1 }}
                    />
                ) : error ? (
                    <Typography
                        sx={{ fontSize: '12px', color: 'error.main', py: 4 }}
                    >
                        Failed to load aggregate series: {error}
                    </Typography>
                ) : combined.length < 2 ? (
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.disabled',
                            py: 4,
                            textAlign: 'center',
                        }}
                    >
                        Not enough series data to plot a combined curve.
                    </Typography>
                ) : (
                    <TraderPnlChart
                        primaryPoints={combined}
                        flowPoints={NO_FLOWS}
                        chartMode={mode}
                        flowMode="deposit_withdraw"
                        zoomDomain={zoom}
                        globalDomain={globalDomain}
                        onZoom={setZoom}
                    />
                )}
            </Box>
        </SectionCard>
    );
}